<template>
  <div class="admin-dashboard">
    <header class="header">
      <h1 class="titulo">👥 Status - Hoje</h1>
    </header>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Carregando registros...</p>
    </div>

    <!-- Nenhum registro -->
    <div v-else-if="ultimosStatus.length === 0" class="empty-state">
      <i class="fas fa-user-clock empty-icon"></i>
      <h3>Nenhum registro encontrado</h3>
      <p>Não há registros para hoje.</p>
    </div>

    <!-- Lista -->
    <div v-else class="status-list">
      <div
        v-for="func in ultimosStatus"
        :key="func.id"
        class="status-card"
      >
        <div class="card-header">
          <h3 class="nome">{{ func.nome }}</h3>
          <span class="status-badge" :class="statusClass(func.ultimo_registro_tipo)">
            {{ func.ultimo_registro_tipo }}
          </span>
        </div>

        <div class="card-body">
          <p><strong>🕒 Entrada:</strong> {{ formatDate(func.primeiro_registro) }}</p>
          <p><strong>⏱ Último Status:</strong> {{ formatDate(func.ultimo_registro) }}</p>
        </div>

        <div class="card-footer">
          <button class="btn-detalhes" @click="abrirModal(func.id)">
            Ver detalhes
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Detalhes -->
    <transition name="fade">
      <div v-if="showModal" class="modal-overlay" @click.self="fecharModal">
        <div class="modal">
          <button class="modal-close" @click="fecharModal">✕</button>
          <h3 class="modal-title">Eventos de {{ modalFuncionario?.nome }}</h3>

          <div v-if="modalEventos.length === 0" class="empty-modal">
            <i class="fas fa-history"></i>
            <p>Nenhum evento registrado para hoje.</p>
          </div>

          <div v-else class="timeline">
            <div v-for="(evento, index) in modalEventos" :key="index" class="timeline-item">
              <div class="marker" :class="statusClass(evento.registro_tipo)"></div>
              <div class="timeline-content">
                <p class="timestamp">{{ formatDate(evento.registro_timestamp) }}</p>
                <p class="tipo" :class="statusClass(evento.registro_tipo)">
                  {{ evento.registro_tipo }}
                </p>
                <div class="acao-linha">
                  <span class="coords" v-if="evento.latitude && evento.longitude">
                    Lat: {{ evento.latitude.toFixed(5) }} | Lng: {{ evento.longitude.toFixed(5) }}
                  </span>
                  <button
                    v-if="evento.latitude && evento.longitude"
                    class="btn-mapa"
                    @click="abrirMapaEvento(evento)"
                  >
                    Ver no mapa
                  </button>
                </div>
              </div>
              <div v-if="index < modalEventos.length - 1" class="timeline-line"></div>
            </div>
          </div>

          <button class="btn-principal" @click="fecharModal">Fechar</button>
        </div>
      </div>
    </transition>

    <!-- Modal Mapa -->
    <transition name="fade">
      <div v-if="mostrarModalMapa" class="modal-overlay" @click.self="fecharModalMapa">
        <div class="modal mapa-modal">
          <button class="modal-close" @click="fecharModalMapa">✕</button>
          <h3 class="modal-title">📍 Local do evento</h3>
          <p class="modal-subtitle">{{ eventoSelecionado?.registro_tipo }} — {{ formatDate(eventoSelecionado?.registro_timestamp) }}</p>
          <div class="mapa-container" ref="mapaContainer"></div>
          <p class="coords" v-if="eventoSelecionado">
            Lat: {{ eventoSelecionado.latitude }} | Lng: {{ eventoSelecionado.longitude }}
          </p>
          <button class="btn-principal" @click="fecharModalMapa">Fechar</button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { adminService } from '@/services/adminService';
import L from 'leaflet';

export default {
  name: 'DashboardStatus',
  data() {
    return {
      funcionarios: [],
      ultimosStatus: [],
      loading: false,
      showModal: false,
      modalFuncionario: null,
      modalEventos: [],
      mostrarModalMapa: false,
      eventoSelecionado: null,
      mapa: null
    };
  },
  mounted() {
    this.carregarStatusHoje();
  },
  methods: {
    async carregarStatusHoje() {
      this.loading = true;
      try {
        const response = await adminService.getDashboardStats();
        this.funcionarios = response.data;

        const grouped = {};
        this.funcionarios.forEach(evento => {
          if (!grouped[evento.id]) grouped[evento.id] = [];
          grouped[evento.id].push(evento);
        });

        this.ultimosStatus = Object.values(grouped).map(events => {
          const sorted = events.sort(
            (a, b) => new Date(a.registro_timestamp) - new Date(b.registro_timestamp)
          );
          const ultimo = sorted[sorted.length - 1];
          return {
            id: ultimo.id,
            nome: ultimo.nome,
            primeiro_registro: sorted[0].registro_timestamp,
            ultimo_registro: ultimo.registro_timestamp,
            ultimo_registro_tipo: ultimo.registro_tipo
          };
        });
      } catch (error) {
        console.error('Erro ao carregar status:', error);
        alert(error.response?.data?.detail || 'Erro ao carregar status');
      } finally {
        this.loading = false;
      }
    },
    formatDate(ts) {
      if (!ts) return '-';
      return new Date(ts).toLocaleString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },
    statusClass(status) {
      if (!status) return '';
      const s = status.toLowerCase();
      if (s.includes('entrada') || s.includes('trabalhando')) return 'status-trabalhando';
      if (s.includes('pausa')) return 'status-pausa';
      if (s.includes('saida') || s.includes('inativo')) return 'status-inativo';
      return 'status-default';
    },
    abrirModal(funcionarioId) {
      this.modalEventos = this.funcionarios
        .filter(e => e.id === funcionarioId)
        .sort((a, b) => new Date(a.registro_timestamp) - new Date(b.registro_timestamp));

      this.modalFuncionario = this.ultimosStatus.find(f => f.id === funcionarioId);
      this.showModal = true;
    },
    fecharModal() {
      this.showModal = false;
      this.modalFuncionario = null;
      this.modalEventos = [];
      this.fecharModalMapa();
    },
    abrirMapaEvento(evento) {
      this.eventoSelecionado = evento;
      this.mostrarModalMapa = true;
      this.$nextTick(() => {
        this.renderizarMapaEvento();
      });
    },
    renderizarMapaEvento() {
      if (!this.eventoSelecionado || !this.eventoSelecionado.latitude || !this.eventoSelecionado.longitude) return;
      const center = [this.eventoSelecionado.latitude, this.eventoSelecionado.longitude];

      if (this.mapa) {
        this.mapa.remove();
        this.mapa = null;
      }

      this.mapa = L.map(this.$refs.mapaContainer, {
        zoomControl: true,
        scrollWheelZoom: true,
      }).setView(center, 17);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(this.mapa);

      L.marker(center).addTo(this.mapa).bindPopup(this.eventoSelecionado.registro_tipo || 'Evento');
    },
    fecharModalMapa() {
      this.mostrarModalMapa = false;
      this.eventoSelecionado = null;
      if (this.mapa) {
        this.mapa.remove();
        this.mapa = null;
      }
    }
  }
};
</script>

<style scoped>
/* Estrutura */
.admin-dashboard {
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1rem;
  background: #f8fafc;
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
}
.header {
  text-align: center;
  margin-bottom: 1.5rem;
}
.titulo {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
}

/* Loading */
.loading-state {
  text-align: center;
  padding: 2rem;
  color: #475569;
}
.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  margin: 0 auto 1rem;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Empty */
.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #64748b;
}
.empty-icon {
  font-size: 2.5rem;
  color: #cbd5e1;
  margin-bottom: 0.5rem;
}

/* Lista */
.status-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.status-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  padding: 1rem 1.2rem;
  display: flex;
  flex-direction: column;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.6rem;
}
.nome {
  font-weight: 700;
  color: #1e293b;
}
.status-badge {
  font-weight: 600;
  padding: 0.3rem 0.6rem;
  border-radius: 8px;
  text-transform: capitalize;
  font-size: 0.9rem;
}
.status-trabalhando { background: #dcfce7; color: #166534; }
.status-pausa { background: #fef9c3; color: #854d0e; }
.status-inativo { background: #fee2e2; color: #991b1b; }
.status-default { background: #e2e8f0; color: #334155; }

.card-body p {
  margin: 0.25rem 0;
  color: #334155;
  font-size: 0.9rem;
}
.card-footer {
  margin-top: 0.8rem;
  text-align: right;
}
.btn-detalhes {
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s;
}
.btn-detalhes:hover {
  background: #2563eb;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  padding: 1rem;
}
.modal {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  width: 100%;
  max-width: 500px;
  max-height: 80%;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  position: relative;
  animation: scaleIn 0.25s ease;
}
.modal-title {
  text-align: center;
  font-weight: 700;
  font-size: 1.25rem;
  color: #1e293b;
  margin-bottom: 1rem;
}
.modal-close {
  position: absolute;
  top: 12px;
  right: 14px;
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: #64748b;
}
.modal-close:hover { color: #1e293b; }

/* Timeline no modal */
.timeline {
  display: flex;
  flex-direction: column;
  position: relative;
  margin: 1rem 0;
}
.timeline-item {
  position: relative;
  padding-left: 2rem;
  margin-bottom: 1rem;
}
.marker {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  position: absolute;
  left: 0;
  top: 4px;
  border: 2px solid white;
  box-shadow: 0 0 0 2px #cbd5e1;
}
.timeline-line {
  position: absolute;
  left: 6px;
  top: 18px;
  width: 2px;
  height: calc(100% - 6px);
  background: #cbd5e1;
}
.timeline-content {
  background: #f9fafb;
  border-radius: 10px;
  padding: 0.6rem 0.8rem;
}
.timestamp {
  font-size: 0.8rem;
  color: #64748b;
}
.tipo {
  font-weight: 700;
  font-size: 0.9rem;
}
.local {
  font-size: 0.85rem;
  color: #334155;
}

.acao-linha {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.4rem;
}

.coords {
  font-size: 0.85rem;
  color: #475569;
}

.btn-mapa {
  background: #e2e8f0;
  color: #0f172a;
  border: none;
  border-radius: 8px;
  padding: 0.35rem 0.6rem;
  font-weight: 600;
  cursor: pointer;
}
.btn-mapa:hover {
  background: #cbd5e1;
}

.mapa-modal .mapa-container {
  height: 260px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  margin: 0.6rem 0;
}
/* Botão principal modal */
.btn-principal {
  width: 100%;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 0.8rem;
  font-weight: 600;
  margin-top: 1rem;
  transition: 0.2s;
}
.btn-principal:hover {
  background: #059669;
}

/* Animações */
.fade-enter-active, .fade-leave-active {
  transition: all 0.25s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
@keyframes scaleIn {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
</style>
